<template>
  <div class="data-filter" :class="{ 'is-open': filterOpen }">
    <button
      type="button"
      class="filter-toggle"
      :aria-expanded="filterOpen ? 'true' : 'false'"
      @click="filterOpen = !filterOpen">
      <span class="filter-toggle__summary">{{ filterSummary }}</span>
      <span class="filter-toggle__action">{{ filterOpen ? 'Hide' : 'Filters' }}</span>
    </button>

    <div class="filter-panel">
    <div class="filter-row">
    <!-- date filter -->
    <div v-if="!hideDate" class="filter-item filter-item--date">
      <span class="text-muted small text-left font-weight-bold d-block">Select Range</span>
      <date-time-picker v-model="dateRange" is-range :masks="masks" />
    </div>

    <div v-if="!hideInterval" class="filter-item">
      <span class="text-muted small text-left font-weight-bold d-block">Interval</span>
      <select name="" class="form-control" v-model="selectedInterval">
        <option v-if="minute" value="minute">Minute</option>
        <option value="hour">Hour</option>
        <option value="day">Day</option>
        <option value="week">Week</option>
      </select>
    </div>

    <div v-if="!hideRangeType" class="filter-item">
      <span class="text-muted small text-left font-weight-bold d-block">Range Type</span>
      <select name="" class="form-control" v-model="selectedRangeType">
        <option value="lastMonth">Last Month</option>
        <option value="thisMonth">This Month</option>
        <option value="lastWeek">Last Week</option>
        <option value="thisWeek">This Week</option>
        <option value="yesterday">Yesterday</option>
        <option value="today">Today</option>
        <option value="custom">Custom</option>
      </select>
    </div>

    <div class="filter-item">
      <span class="text-muted small text-left font-weight-bold d-block">Asset</span>
      <select name="" class="form-control" v-model="selectedAsset">
        <option v-for="asset in assets" :key="asset.massId" :value="asset.massId">{{ asset.massName }}</option>
      </select>
    </div>

    <div class="filter-item filter-item--action">
      <span class="text-muted small font-weight-bold d-none d-md-block">&nbsp;</span>
      <button class="btn btn-mantraz btn-apply w-100" @click="handleApply">Apply</button>
    </div>

    <div v-if="!hideReport" class="filter-item filter-item--action">
      <span class="text-muted small font-weight-bold d-none d-md-block">&nbsp;</span>
      <ReportDownloadDropdown />
    </div>

    <div v-if="downloadExcel" class="filter-item filter-item--action">
      <span class="text-muted small font-weight-bold d-none d-md-block">&nbsp;</span>
      <button class="btn btn-success w-100" @click="onDownloadExcel">
        Download XLSX
      </button>
    </div>
    </div>
    <div v-if="isFilterDirty" class="text-warning mt-2 mx-2">
      Filters have changed. Click Apply to update the data.
    </div>
    <div v-if="pointHint" class="text-warning mt-2 mx-2">{{ pointHint }}</div>
    </div>
  </div>
</template>

<script>
import moment from 'moment';
import { mapState } from 'vuex';
import ReportDownloadDropdown from '@/components/report/ReportDownloadDropdown.vue';
import DateTimePicker from '@/components/directives/DateTimePicker.vue';
import dateUtil from '@/util/date';
import { suggestInterval, estimatePointCount } from '@/util/rangeDisplay';

export default {
  name: 'DataFilter',
  components: {
    ReportDownloadDropdown,
    DateTimePicker,
  },
  props: {
    onApplyFilter: {
      type: Function,
      required: true,
    },
    hideReport: {
      type: Boolean,
      required: false,
    },
    downloadExcel: {
      type: Boolean,
      required: false,
    },
    onDownloadExcel: {
      type: Function,
      required: false,
    },
    minute: {
      type: Boolean,
      required: false,
    },
    hideDate: {
      type: Boolean,
      required: false,
      default: false,
    },
    hideRangeType: {
      type: Boolean,
      required: false,
      default: false,
    },
    hideInterval: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {
      masks: 'DD-MM-YYYY HH:mm',
      appliedSnapshot: null,
      lastSuggestedInterval: undefined,
      filterOpen: false,
    };
  },
  computed: {
    currAsset() {
      const currMassId = this.$store.getters['filter/currentMassId'];
      const assets = this.$store.getters['asset/assets'];
      if (!assets) return null;

      return assets.find((asset) => asset.massId === currMassId);
    },
    dateRange: {
      get() {
        const { start, end } = this.$store.getters['filter/date'];
        const startDate = new Date(moment(start).valueOf());
        const endDate = new Date(moment(end).valueOf());

        return { start: startDate, end: endDate };
      },
      set(val) {
        const start = moment(val.start);
        const end = moment(val.end);

        this.$store.dispatch('filter/setCustomDate', { start, end });
        this.suggestIntervalForRange(start.valueOf(), end.valueOf());
      },
    },
    ...mapState('asset', ['assets']),
    ...mapState('filter', {
      currentMassId: (state) => state.currentMassId,
      interval: (state) => state.interval,
      rangeType: (state) => state.rangeType,
      isLoading: (state) => state.isLoading,
    }),
    selectedInterval: {
      get() {
        return this.interval;
      },
      set(val) {
        this.$store.dispatch('filter/setInterval', val);
      },
    },
    selectedAsset: {
      get() {
        return this.currentMassId;
      },
      set(val) {
        this.$store.dispatch('filter/setCurrentMassId', val);
      },
    },
    selectedRangeType: {
      get() {
        return this.rangeType;
      },
      set(val) {
        this.$store.dispatch('filter/setRangeType', val);
      },
    },
    isFilterDirty() {
      if (!this.appliedSnapshot) return false;
      const current = this.getSnapshot();
      const applied = this.appliedSnapshot;

      return (
        (!this.hideDate && (current.start !== applied.start || current.end !== applied.end))
        || (!this.hideInterval && current.interval !== applied.interval)
        || (!this.hideRangeType && current.rangeType !== applied.rangeType)
        || String(current.massId) !== String(applied.massId)
      );
    },
    // Dropdown ini cuma nawarin hour/day/week, plus minute kalau diizinkan.
    intervalOptions() {
      return dateUtil.dataIntervalOptions.filter((option) => (
        option.value === 'MINUTE' ? this.minute : ['HOUR', 'DAY', 'WEEK'].includes(option.value)
      ));
    },
    intervalSeconds() {
      const hit = this.intervalOptions.find(
        (option) => option.value.toLowerCase() === String(this.selectedInterval).toLowerCase(),
      );
      return hit ? hit.seconds : 0;
    },
    pointCount() {
      const { start, end } = this.$store.getters['filter/date'];
      if (!start || !end) return 0;
      return estimatePointCount(moment(start).valueOf(), moment(end).valueOf(), this.intervalSeconds);
    },
    pointHint() {
      if (this.pointCount <= 1500) return '';
      return `~${this.pointCount.toLocaleString('en-US')} points — this may take a while`;
    },
    // Isi bar ringkas pas filter dilipat di layar kecil.
    filterSummary() {
      const asset = this.currAsset ? this.currAsset.massName : 'No asset';
      if (this.hideDate) return asset;
      const { start, end } = this.$store.getters['filter/date'];
      if (!start || !end) return asset;
      return `${asset} · ${moment(start).format('DD MMM HH:mm')} – ${moment(end).format('DD MMM HH:mm')}`;
    },
  },
  mounted() {
    this.markApplied();
  },
  methods: {
    getSnapshot() {
      const { start, end } = this.$store.getters['filter/date'];
      return {
        start: start ? moment(start).valueOf() : null,
        end: end ? moment(end).valueOf() : null,
        interval: this.$store.getters['filter/interval'],
        rangeType: this.$store.getters['filter/rangeType'],
        massId: this.$store.getters['filter/currentMassId'],
      };
    },
    // Patokan "Filters have changed"; halaman panggil ini habis load.
    markApplied() {
      this.appliedSnapshot = this.getSnapshot();
    },
    async handleApply() {
      this.filterOpen = false;
      await this.onApplyFilter();
      this.markApplied();
    },
    // Cuma timpa kalau sarannya berubah, biar pilihan manual user ga kereset.
    suggestIntervalForRange(startMs, endMs) {
      if (this.hideInterval) return;
      const suggested = suggestInterval(startMs, endMs, this.intervalOptions);
      if (!suggested || suggested === this.lastSuggestedInterval) return;
      this.lastSuggestedInterval = suggested;
      this.selectedInterval = suggested.toLowerCase();
    },
  },
};
</script>

<style scoped>
.form-control {
  border: 1px solid #dee2e6 !important;
}

/* Nempel di bawah navbar yang tingginya 70px. */
.data-filter {
  position: sticky;
  top: 70px;
  z-index: 1020;
  background-color: #fff;
  padding: 8px 0.5rem 0;
}

/* Tombol lipat cuma nongol di layar kecil. */
.filter-toggle {
  display: none;
}

/* Filter layout */
.filter-row {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 8px;
  padding-bottom: 8px;
}

.filter-item {
  flex: 1;
  min-width: 140px;
}

.filter-item--date {
  min-width: 220px;
}

.filter-item--action {
  flex: 0 0 auto;
}

@media (max-width: 575px) {
  .filter-item,
  .filter-item--date,
  .filter-item--action {
    flex: 0 0 100%;
    min-width: 0;
  }

  .data-filter {
    padding: 0;
  }

  .filter-toggle {
    align-items: center;
    background-color: #fff;
    border: 0;
    border-bottom: 1px solid #e6eaf0;
    display: flex;
    gap: 8px;
    justify-content: space-between;
    padding: 10px 0.75rem;
    text-align: left;
    width: 100%;
  }

  .filter-toggle__summary {
    color: #495057;
    font-size: 0.8125rem;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .filter-toggle__action {
    color: #ff69b4;
    flex: 0 0 auto;
    font-weight: 600;
  }

  /* Panel melayang, biar kotak sticky-nya tetap setinggi satu baris. */
  .filter-panel {
    display: none;
  }

  .data-filter.is-open .filter-panel {
    background-color: #fff;
    box-shadow: 0 8px 16px rgba(0, 0, 0, 0.12);
    display: block;
    left: 0;
    max-height: calc(100vh - 130px);
    overflow-y: auto;
    padding: 8px 0.5rem;
    position: absolute;
    right: 0;
    top: 100%;
  }
}
</style>
